import React from "react";
import { Link } from "react-router-dom";
import {
    Home as HomeIcon,
    Building2,
    ShieldCheck,
    Sparkles,
    Compass,
    Clock4,
    Key,
    ArrowUpRight,
} from "lucide-react";
import { SERVICE_SECTIONS } from "./Navbar";

// Retail-focused service set — residential mortgages, BTL, residential bridging, protection.
const SERVICES = [
    {
        key: "what-we-do",
        eyebrow: "Complex decisions, made simple",
        title: "What We Do",
        desc: "A UK-based mortgage and protection advisory firm delivering clear, expert guidance tailored to your personal needs.",
        icon: Compass,
        span: "md:col-span-5 md:row-span-2",
        image:
            "https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1400&q=85",
    },
    {
        key: "residential-mortgages",
        eyebrow: "First home or your forever home",
        title: "Residential Mortgages",
        desc: "First-time buyer, home mover and remortgage advice — matched to a panel of 90+ UK lenders.",
        icon: HomeIcon,
        span: "md:col-span-4",
        image:
            "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=85",
    },
    {
        key: "buy-to-let",
        eyebrow: "Build your portfolio",
        title: "Buy-to-Let Mortgages",
        desc: "Personal-name and SPV (limited company) BTL — including HMO, holiday lets and portfolio landlords.",
        icon: Key,
        span: "md:col-span-3",
        image:
            "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1400&q=85",
    },
    {
        key: "residential-bridging",
        eyebrow: "Move fast, refinance later",
        title: "Residential Bridging",
        desc: "Short-term residential bridging finance — chain-breaks, auction purchases, light refurbishment.",
        icon: Clock4,
        span: "md:col-span-4",
        image:
            "https://images.unsplash.com/photo-1515263487990-61b07816b324?auto=format&fit=crop&w=1400&q=85",
    },
    {
        key: "protection-insurance",
        eyebrow: "Protect what matters most",
        title: "Protection & Insurance",
        desc: "Life cover, critical illness, income protection, home insurance and landlord insurance — with referrals for PMI and estate planning.",
        icon: ShieldCheck,
        span: "md:col-span-4",
        image:
            "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=85",
    },
    {
        key: "our-approach",
        eyebrow: "Any time you need us",
        title: "Our Approach",
        desc: "Personalised, transparent and fully compliant guidance — designed around your circumstances, goals and concerns.",
        icon: Sparkles,
        span: "md:col-span-4",
        image:
            "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=85",
    },
    {
        key: "about-us",
        eyebrow: "Advice you can trust",
        title: "About Us",
        desc: "A mortgage and protection advisory firm — appointed representative of HL Partnership — shaped by transparency, professionalism and lasting relationships.",
        icon: Building2,
        span: "md:col-span-12",
        image:
            "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1400&q=85",
    },
];

export default function ServicesGrid() {
    return (
        <section
            data-testid="services-section"
            id="services"
            className="relative py-14 md:py-20"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mb-16">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-semibold">
                                Your goals. Our priority.
                            </span>
                        </div>
                        <h2 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-primary leading-[1.05]">
                            Complex decisions,
                            <br />
                            <em className="italic font-light">simplified.</em>
                        </h2>
                    </div>
                    <div className="md:col-span-6 md:col-start-7 flex items-end">
                        <p className="text-lg text-brand-muted leading-relaxed">
                            We combine market-leading products with personal service to
                            deliver results, even in complex cases. When challenges
                            arise, we find solutions — professionally, transparently,
                            and efficiently.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(220px,auto)]">
                    {SERVICES.map((s, i) => {
                        const Icon = s.icon;
                        const idx = SERVICE_SECTIONS.findIndex((x) => x.id === s.key);
                        return (
                            <article
                                key={s.key}
                                data-testid={`service-card-${s.key}`}
                                className={`group relative overflow-hidden bg-brand-surface border border-brand-border p-8 md:p-10 transition-all duration-500 hover:-translate-y-1 hover:border-brand-primary ${s.span || "md:col-span-4"}`}
                            >
                                {s.image && (
                                    <div className="absolute inset-0 -z-0">
                                        <img
                                            src={s.image}
                                            alt=""
                                            className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-700"
                                            loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-br from-brand-surface/85 via-brand-surface/55 to-brand-surface/15" />
                                    </div>
                                )}
                                <div className="relative z-10 h-full flex flex-col">
                                    <div className="flex items-start justify-between mb-8">
                                        <div className="h-12 w-12 rounded-full bg-brand-primary/8 flex items-center justify-center text-brand-primary group-hover:bg-brand-primary group-hover:text-brand-bg transition-colors">
                                            <Icon size={20} />
                                        </div>
                                        <div className="text-xs tracking-[0.2em] text-brand-muted">
                                            {String(idx + 1).padStart(2, "0")}
                                        </div>
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-brand-accent font-semibold mb-2">
                                        {s.eyebrow}
                                    </div>
                                    <h3 className="font-serif-editorial text-3xl text-brand-primary mb-3">
                                        {s.title}
                                    </h3>
                                    <p className="text-brand-muted text-base leading-relaxed max-w-md">
                                        {s.desc}
                                    </p>
                                    <div className="mt-auto pt-8">
                                        <Link
                                            to={`/services#${s.key}`}
                                            data-testid={`service-link-${s.key}`}
                                            className="inline-flex items-center gap-2 text-sm font-medium text-brand-primary link-underline"
                                        >
                                            Read more
                                            <ArrowUpRight
                                                size={14}
                                                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                            />
                                        </Link>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
